import { ChevronDown } from 'lucide-react'
import type { ReviewChaseRowConfidence } from '@natalan/capehost-contracts'
import { TableRow, TableCell } from '@/components/ui/table'
import { ConfidenceDetail, PANEL_TIER_CLASSES } from '@/components/review-chase/confidence-detail'
import {
  useReviewConfidence,
  getConfidenceTier,
  type ConfidenceTier,
} from '@/hooks/use-review-confidence'

const TOGGLE_TIER_CLASSES: Record<ConfidenceTier, string> = {
  high: 'bg-mint/10 text-mint border-mint/40',
  mid: 'bg-amber/10 text-amber border-amber/30',
  low: 'bg-danger/10 text-danger border-danger/30',
}

interface ConfidenceToggleProps {
  reservationId: string | null
  isExpanded: boolean
  onToggle: () => void
  /**
   * The summary the digest row already carries (CAP-744). Pass it - `null`
   * when the row has none - and the pill renders from it with no request
   * of its own; only a `queued` summary polls the per-reservation endpoint,
   * until the score lands. Leave it `undefined` and the toggle fetches the
   * score itself.
   */
  embedded?: ReviewChaseRowConfidence | null
}

/**
 * The collapsed, always-visible affordance: a single labeled, colored pill
 * reading "⭐ Review confidence · 31" - what it is and what clicking it does
 * are both stated up front, not left to an icon-only button and a tooltip
 * (the way SuggestionToggleButton's Sparkles icon works for guest-review
 * suggestions). Renders nothing while there's no score to show (queued
 * briefly excepted) - no empty/dead control for a reservation that was
 * never eligible for one.
 */
export function ConfidenceToggle({
  reservationId,
  isExpanded,
  onToggle,
  embedded,
}: ConfidenceToggleProps) {
  const shouldFetch = embedded === undefined || embedded?.status === 'queued'
  const fetched = useReviewConfidence(reservationId, { enabled: shouldFetch })
  const confidence = shouldFetch ? fetched.confidence : (embedded ?? null)

  if (shouldFetch && (fetched.isQueued || (fetched.isLoading && !confidence))) {
    return (
      <span className="inline-flex animate-pulse items-center gap-1.5 rounded-full border border-border bg-surface2 px-2.5 py-0.5 text-xs font-semibold text-textMuted">
        ⭐ Review confidence…
      </span>
    )
  }

  if (!confidence || confidence.status !== 'completed' || confidence.score == null) return null

  const tier = getConfidenceTier(confidence.score)

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors hover:brightness-110 ${TOGGLE_TIER_CLASSES[tier]}`}
    >
      ⭐ Review confidence · {confidence.score}
      <ChevronDown className={`h-3 w-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
    </button>
  )
}

/**
 * The one definition of "this reservation has a score worth rendering",
 * shared by the panel and the table row that wraps it - a completed record
 * carrying an actual number, with its tier resolved.
 */
function useScoredConfidence(reservationId: string | null, isExpanded: boolean) {
  // The signals behind a score are not on the digest row, so they are asked
  // for when the pill is opened, not on every page load (CAP-744).
  const { confidence, recommendation, propertyPrior } = useReviewConfidence(reservationId, {
    enabled: isExpanded,
  })

  if (!confidence || confidence.status !== 'completed' || confidence.score == null) return null

  return { confidence, recommendation, propertyPrior, tier: getConfidenceTier(confidence.score) }
}

interface ConfidencePanelProps {
  reservationId: string | null
  isExpanded: boolean
}

/**
 * The expanded detail, outside a table (ConfidenceRow puts it in one). Reads
 * the same query cache entry ConfidenceToggle already populated for this
 * reservationId (TanStack Query dedupes by queryKey), so expanding is
 * instant rather than a second network round trip.
 */
export function ConfidencePanel({ reservationId, isExpanded }: ConfidencePanelProps) {
  const scored = useScoredConfidence(reservationId, isExpanded)

  if (!isExpanded || !scored) return null

  const { confidence, recommendation, propertyPrior, tier } = scored

  return (
    <div className={`space-y-2 rounded-lg border px-4 py-3 ${PANEL_TIER_CLASSES[tier]}`}>
      <span className="text-xs font-medium text-textMuted">
        Five-star confidence · {confidence.score}/100
      </span>
      <ConfidenceDetail
        confidence={confidence}
        tier={tier}
        recommendation={recommendation}
        propertyPrior={propertyPrior}
      />
    </div>
  )
}

/**
 * The Review Chase table's wrapper around ConfidencePanel - its own table
 * row, colSpan 3, mirroring SuggestionRow.
 */
export function ConfidenceRow({ reservationId, isExpanded }: ConfidencePanelProps) {
  // Asked here as well as inside ConfidencePanel (one shared cache entry, no
  // second fetch) so the table emits no empty row when the panel would
  // render nothing - both sides read the one definition of "has a score".
  const scored = useScoredConfidence(reservationId, isExpanded)

  if (!isExpanded || !scored) return null

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={3} className="pb-3 pt-0">
        <ConfidencePanel reservationId={reservationId} isExpanded={isExpanded} />
      </TableCell>
    </TableRow>
  )
}
